import type {
  AllDayInput,
  AllDayItem,
  DaySleep,
  SleepTimes,
  Checkin,
  NotificationSettings,
  DayOverride,
  DayTemplate,
  DayTemplateInput,
  ExternalEvent,
  Goal,
  GoalInput,
  PlanContext,
  PlanningSettings,
  PlanPeriod,
  PlanPeriodInput,
  ScheduleTaskInput,
  Task,
  TaskInput,
  TaskPatch,
  WeekPattern,
  WeekPatternInput,
  ExceptionInput,
  Item,
  ItemException,
  ItemInput,
  ItemPatch,
  Settings,
  SecurityInfo,
} from '@planer/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`API ${status}`);
  }
}

/** Thrown when the request never reached the server (offline, DNS, …). */
export class NetworkError extends Error {}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
export const onUnauthorized = (fn: Listener) => {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
};

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : 'network');
  }
  if (res.status === 401 && path !== '/auth/login') {
    unauthorizedListeners.forEach((fn) => {
      fn();
    });
  }
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export interface Schedule {
  items: Item[];
  exceptions: ItemException[];
}

/** Templates, patterns, periods, goals and inbox tasks. */
export type Plan = PlanContext & { goals: Goal[]; tasks: Task[] };

export const api = {
  login: (password: string) => request<{ ok: true }>('POST', '/auth/login', { password }),
  logout: () => request<{ ok: true }>('POST', '/auth/logout'),
  session: () => request<{ authenticated: boolean }>('GET', '/auth/session'),
  streaks: () => request<{ streaks: Record<string, number> }>('GET', '/streaks'),
  security: () => request<SecurityInfo>('GET', '/auth/security'),
  changePassword: (current: string, next: string) =>
    request<{ ok: true }>('POST', '/auth/password', { current, next }),
  logoutSession: (id: string) => request<undefined>('DELETE', `/auth/sessions/${id}`),
  logoutOthers: () => request<{ loggedOut: number }>('POST', '/auth/sessions/logout-others'),
  settings: () => request<Settings>('GET', '/settings'),
  schedule: () => request<Schedule>('GET', '/schedule'),
  createItem: (input: ItemInput) => request<Item>('POST', '/items', input),
  updateItem: (id: string, patch: ItemPatch) =>
    request<{ item: Item; previous: Item | null }>('PATCH', `/items/${id}`, patch),
  deleteItem: (id: string, scope: 'all' | 'following' = 'all', fromDate?: string) =>
    request<undefined>(
      'DELETE',
      `/items/${id}?${new URLSearchParams({ scope, ...(fromDate ? { fromDate } : {}) }).toString()}`,
    ),
  restoreItem: (id: string) => request<Item>('POST', `/items/${id}/restore`),
  putException: (id: string, date: string, input: ExceptionInput) =>
    request<ItemException>('PUT', `/items/${id}/exceptions/${date}`, input),
  deleteException: (id: string, date: string) =>
    request<undefined>('DELETE', `/items/${id}/exceptions/${date}`),
  checkins: (from: string, to: string) =>
    request<{ checkins: Checkin[] }>('GET', `/checkins?from=${from}&to=${to}`),
  events: (from: string, to: string) =>
    request<{ events: ExternalEvent[] }>('GET', `/events?from=${from}&to=${to}`),
  calendars: () => request<CalendarsState>('GET', '/calendars'),
  addIcs: (name: string, url: string) =>
    request<{ accounts: CalendarAccount[] }>('POST', '/calendars/ics', { name, url }),
  addCaldav: (username: string, password: string) =>
    request<{ accounts: CalendarAccount[] }>('POST', '/calendars/caldav', { username, password }),
  startOAuth: (provider: 'google' | 'microsoft') =>
    request<{ url: string }>('POST', `/calendars/oauth/${provider}/start`),
  updateSource: (
    id: string,
    patch: { enabled?: boolean; requiresCheck?: boolean; color?: string },
  ) => request<{ accounts: CalendarAccount[] }>('PATCH', `/calendars/sources/${id}`, patch),
  saveOAuthClient: (
    provider: 'google' | 'microsoft',
    client: { clientId: string; clientSecret: string; tenant?: string },
  ) => request<undefined>('PUT', `/calendars/oauth/${provider}/client`, client),
  deleteOAuthClient: (provider: 'google' | 'microsoft') =>
    request<undefined>('DELETE', `/calendars/oauth/${provider}/client`),
  deleteAccount: (id: string) => request<undefined>('DELETE', `/calendars/accounts/${id}`),
  syncCalendars: () => request<{ accounts: CalendarAccount[] }>('POST', '/calendars/sync'),
  plan: () => request<Plan>('GET', '/plan'),
  putPlanSettings: (settings: PlanningSettings) =>
    request<PlanningSettings>('PUT', '/plan/settings', settings),
  createTemplate: (input: DayTemplateInput) => request<DayTemplate>('POST', '/templates', input),
  updateTemplate: (id: string, input: Partial<DayTemplateInput>) =>
    request<DayTemplate>('PATCH', `/templates/${id}`, input),
  duplicateTemplate: (id: string) => request<DayTemplate>('POST', `/templates/${id}/duplicate`),
  deleteTemplate: (id: string) => request<undefined>('DELETE', `/templates/${id}`),
  createPattern: (input: WeekPatternInput) => request<WeekPattern>('POST', '/patterns', input),
  updatePattern: (id: string, input: WeekPatternInput) =>
    request<WeekPattern>('PUT', `/patterns/${id}`, input),
  deletePattern: (id: string) => request<undefined>('DELETE', `/patterns/${id}`),
  createPeriod: (input: PlanPeriodInput) => request<PlanPeriod>('POST', '/periods', input),
  updatePeriod: (id: string, input: PlanPeriodInput) =>
    request<PlanPeriod>('PUT', `/periods/${id}`, input),
  deletePeriod: (id: string) => request<undefined>('DELETE', `/periods/${id}`),
  setDayTemplate: (date: string, templateId: string | null) =>
    request<DayOverride>('PUT', `/days/${date}/template`, { templateId }),
  clearDayTemplate: (date: string) => request<undefined>('DELETE', `/days/${date}/template`),
  copyDay: (date: string, to: string) =>
    request<{ items: Item[] }>('POST', `/days/${date}/copy`, { to }),
  createAllDay: (input: AllDayInput) => request<AllDayItem>('POST', '/all-day', input),
  updateAllDay: (id: string, input: AllDayInput) =>
    request<AllDayItem>('PUT', `/all-day/${id}`, input),
  deleteAllDay: (id: string) => request<undefined>('DELETE', `/all-day/${id}`),
  restoreAllDay: (id: string) => request<AllDayItem>('POST', `/all-day/${id}/restore`),
  setDaySleep: (date: string, sleep: SleepTimes) =>
    request<DaySleep>('PUT', `/days/${date}/sleep`, sleep),
  clearDaySleep: (date: string) => request<undefined>('DELETE', `/days/${date}/sleep`),
  createGoal: (input: GoalInput) => request<Goal>('POST', '/goals', input),
  updateGoal: (id: string, input: GoalInput) => request<Goal>('PUT', `/goals/${id}`, input),
  archiveGoal: (id: string) => request<undefined>('DELETE', `/goals/${id}`),
  restoreGoal: (id: string) => request<Goal>('POST', `/goals/${id}/restore`),
  createTask: (input: TaskInput) => request<Task>('POST', '/tasks', input),
  updateTask: (id: string, patch: TaskPatch) => request<Task>('PATCH', `/tasks/${id}`, patch),
  scheduleTask: (id: string, input: ScheduleTaskInput) =>
    request<Item>('POST', `/tasks/${id}/schedule`, input),
  unscheduleTask: (id: string) => request<undefined>('DELETE', `/tasks/${id}/schedule`),
  deleteTask: (id: string) => request<undefined>('DELETE', `/tasks/${id}`),
  restoreTask: (id: string) => request<Task>('POST', `/tasks/${id}/restore`),
  push: () => request<PushState>('GET', '/push'),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<undefined>('POST', '/push/subscribe', sub),
  pushUnsubscribe: (endpoint: string) =>
    request<undefined>('POST', '/push/unsubscribe', { endpoint }),
  pushSettings: (settings: NotificationSettings) =>
    request<NotificationSettings>('PUT', '/push/settings', settings),
  pushTest: (endpoint?: string) =>
    request<PushTestResult>('POST', '/push/test', endpoint ? { endpoint } : {}),
  putCheckins: (checkins: Checkin[]) =>
    request<{ applied: number; rejected: { itemId: string; date: string }[] }>('PUT', '/checkins', {
      checkins,
    }),
};

export interface PushState {
  publicKey: string;
  settings: NotificationSettings;
  devices: {
    endpoint: string;
    userAgent: string | null;
    createdAt: string;
    lastSuccessAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
  }[];
}

export interface PushTestResult {
  sent: number;
  failed: { endpoint: string; status: number | null; error: string; removed: boolean }[];
}

export type CalendarProvider = 'google' | 'microsoft' | 'caldav' | 'ics';

export interface CalendarSource {
  id: string;
  name: string;
  color: string;
  enabled: boolean;
  requiresCheck: boolean;
}

export interface CalendarAccount {
  id: string;
  provider: CalendarProvider;
  label: string;
  status: 'ok' | 'error' | 'reauth';
  lastError: string | null;
  lastSyncAt: string | null;
  sources: CalendarSource[];
}

export interface OAuthClientInfo {
  source: 'env' | 'app';
  clientId: string;
  tenant: string;
}

export interface CalendarsState {
  providers: Record<'encryption' | CalendarProvider, boolean>;
  /** Missing from older servers (and from data cached before the update). */
  oauth?: {
    google: OAuthClientInfo | null;
    microsoft: OAuthClientInfo | null;
    redirectUris: Record<'google' | 'microsoft', string>;
  };
  accounts: CalendarAccount[];
}

/** Message the server sent with a 4xx calendar error, if any. */
export function serverMessage(err: unknown): string | null {
  if (
    err instanceof ApiError &&
    err.body &&
    typeof err.body === 'object' &&
    'message' in err.body
  ) {
    const m = (err.body as { message?: unknown }).message;
    return typeof m === 'string' ? m : null;
  }
  return null;
}
